import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadText, toCSV, toRIS, toChicago, toMLA } from '../../utils/citations.js';

export default function ExportMenu({ entries, selectedIds }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const target = selectedIds.size > 0
    ? entries.filter((e) => selectedIds.has(e.id))
    : entries;

  function exportAs(format) {
    const stamp = new Date().toISOString().slice(0, 10);
    if (target.length === 0) return;
    if (format === 'csv') {
      downloadText(toCSV(target), `hermeneutics-${stamp}.csv`, 'text/csv');
    } else if (format === 'ris') {
      downloadText(toRIS(target), `hermeneutics-${stamp}.ris`, 'application/x-research-info-systems');
    } else if (format === 'chicago') {
      downloadText(toChicago(target), `hermeneutics-${stamp}-chicago.txt`);
    } else if (format === 'mla') {
      downloadText(toMLA(target), `hermeneutics-${stamp}-mla.txt`);
    }
    setOpen(false);
  }

  const scopeLabel = selectedIds.size > 0
    ? t('bibliography.export.scopeSelected', { count: selectedIds.size })
    : t('bibliography.export.scopeAll', { count: entries.length });

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-ghost text-sm"
        disabled={target.length === 0}
      >
        {t('bibliography.export.button')}
        <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-lg border border-white/10 bg-navy-base/95 shadow-xl backdrop-blur">
          <div className="border-b border-white/5 px-4 py-2 text-[0.7rem] uppercase tracking-[0.12em] text-ink-muted">
            {scopeLabel}
          </div>
          <button type="button" onClick={() => exportAs('csv')} className="export-item">CSV</button>
          <button type="button" onClick={() => exportAs('ris')} className="export-item">RIS (Zotero, Mendeley)</button>
          <button type="button" onClick={() => exportAs('chicago')} className="export-item">{t('bibliography.export.chicago')}</button>
          <button type="button" onClick={() => exportAs('mla')} className="export-item">{t('bibliography.export.mla')}</button>
        </div>
      )}
    </div>
  );
}
